import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import OrganizationAxiosInstance from "../Services/OrganizationAxiosInstance";
import PassTemplate from "../Modals/PassTemplate";
import { useNavigate } from "react-router-dom";
import PhoneWithPass from "../Components/PhoneWithPass";
import Error from "../Components/Error";
import axios, { isAxiosError } from "axios";
import Spinner from "../Components/Spinner";
import { IoMdAdd } from "react-icons/io";
import DetailsForm from "../Components/DetailsForm";
import { Bounce, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { FaWhatsapp } from "react-icons/fa";
import { HiArrowRight, HiChevronRight } from "react-icons/hi2";

interface ProgramStats {
  points: number;
  rewards: number;
  rewardsRadeemed: number;
  customers: number;
}

interface ProgramWithStats extends PassTemplate {
  stats?: ProgramStats;
  statsLoading: boolean;
}


const Home: React.FC = () => {
  const org = useSelector((state: RootState) => state.auth.org);
  const [programs, setPrograms] = useState<ProgramWithStats[]>([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [modalHidden, setModalHidden] = useState(true);
  const { t } = useTranslation();

  const CreatePassTemplate = async (
    name: string, description: string, pointsToCollect: number, rewardName: string,
    rewardType: "Stamp" | "CouponValue", couponValue: number | null, currencyCode: string | null,
  ) => {
    const toastId = toast.loading("Creating Program", { position: "top-right", theme: "light", isLoading: true, transition: Bounce });

    if (programs.length > 0 && (org?.planName === "Silver" || org?.planName === "Free Trial")) {
      toast.update(toastId, { render: "You can only create one design with Silver Subscription", type: "error", isLoading: false, autoClose: 3000 });
      return;
    }

    try {
      const response = await OrganizationAxiosInstance.post(`/PassTemplate`, {
        Name: name, Description: description, Rewardname: rewardName, PointsToCollect: pointsToCollect,
        RewardType: rewardType, CouponValue: couponValue, CurrencyCode: currencyCode,
      });
      if (response.status === 200) {
        toast.update(toastId, { render: "Program Created Successfully", type: "success", isLoading: false, autoClose: 3000 });
        navigate(`/Dashboard/Program/${response.data.id}/Edit`);
      }
    } catch (error) {
      if (isAxiosError(error)) {
        toast.update(toastId, { render: error.response?.data.error || "Error creating program", type: "error", isLoading: false, autoClose: 3000 });
      }
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const response = await OrganizationAxiosInstance.get("PassTemplate/All");
        const templates: PassTemplate[] = response.data.passTemplates;


        
        setPrograms(templates.map(t => ({ ...t, statsLoading: true })));
        setIsLoading(false);

        templates.forEach(async (tmpl) => {
          try {
            const s = await OrganizationAxiosInstance.get<ProgramStats>(`/PassTemplate/${tmpl.id}/Stats`);
            setPrograms(prev => prev.map(p => p.id === tmpl.id ? { ...p, stats: s.data, statsLoading: false } : p));
          } catch {
            setPrograms(prev => prev.map(p => p.id === tmpl.id ? { ...p, statsLoading: false } : p));
          }
        });
      } catch (error) {
        if (axios.isAxiosError(error)) setError(error.response?.data.error);
        else setError("something went wrong");
        setIsLoading(false);
      }
    };

    fetchAll();

    // Refetch when user navigates back to this tab/page
    window.addEventListener("focus", fetchAll);
    return () => window.removeEventListener("focus", fetchAll);
  }, []);

  const openTemplate = (id: string) => navigate(`/Dashboard/Program/${id}`);

  if (isLoading) return <Spinner loading={isLoading} />;
  if (error) return <Error message={error} />;

  return (
    <div className="space-y-6">

      {/* Greeting */}
      <div>
        <h1 className="text-xl font-bold text-gray-900">
          {t("welcomeBack") || "مرحباً"}{org?.name ? `، ${org.name}` : ""}
        </h1>
        <p className="text-sm text-gray-400 mt-0.5">{t("overviewSubtitle") || "إليك نظرة عامة على أداء برامج الولاء"}</p>
      </div>

      {/* Programs section header */}
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold text-gray-900">{t("programs")}</h2>
        <button
          onClick={() => setModalHidden(false)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 transition shadow-sm"
        >
          <IoMdAdd className="w-4 h-4" />
          {t("createProgram")}
        </button>
      </div>

      {/* Help banner */}
      {programs.length === 0 && (
        <a
          href="https://wa.me/966553608481"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-brand-100 shadow-sm hover:shadow-md transition group"
        >
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
            <FaWhatsapp className="w-5 h-5 text-green-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-800 group-hover:text-brand-600 transition">
              {t("helpBanner") || "تحتاج مساعدة في الإعداد؟ تواصل معنا عبر واتساب"}
            </p>
          </div>
          <HiChevronRight className="w-4 h-4 text-gray-400 group-hover:text-brand-600 transition flex-shrink-0" />
        </a>
      )}

      {/* Programs grid */}
      {programs.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {programs.map((tmpl) => (
            <div
              key={tmpl.id}
              className="bg-white rounded-2xl border border-brand-100 shadow-sm overflow-hidden hover:shadow-md transition group"
            >
              {/* Card preview */}
              <div className="flex justify-center pt-5 pb-3 bg-gray-50">
                <PhoneWithPass
                  backgroundColor={tmpl.backgroundColor}
                  textColor={tmpl.textColor}
                  stripBackgroundColor={tmpl.stripeBackgroundColor}
                  stripBackgroundUrl={tmpl.stripeBackgroundImage}
                  logoUrl={tmpl.logo}
                  pointImageUrl={tmpl.pointImage}
                  pointsToCollect={tmpl.pointsToCollect}
                  points={2}
                  radeemedRewards={0}
                />
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-3 divide-x divide-gray-100 border-t border-gray-100 text-center">
                {[
                  { label: t("customers") || "عملاء", value: tmpl.stats?.customers },
                  { label: t("points") || "نقاط",    value: tmpl.stats?.points },
                  { label: t("redeemed") || "مستبدل", value: tmpl.stats?.rewardsRadeemed },
                ].map(({ label, value }) => (
                  <div key={label} className="py-2.5 px-1">
                    <p className="text-sm font-bold text-gray-900">
                      {tmpl.statsLoading ? <span className="text-gray-300">…</span> : (value ?? 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-400 truncate">{label}</p>
                  </div>
                ))}
              </div>

              {/* Action */}
              <div className="px-4 pb-4 pt-3">
                <button
                  onClick={() => openTemplate(tmpl.id)}
                  className="w-full py-2 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 transition flex items-center justify-center gap-1.5 group-hover:gap-2.5"
                >
                  {t("manage")}
                  <HiArrowRight className="w-3.5 h-3.5 transition-all" />
                </button>
              </div>
            </div>
          ))}

          {/* Add new */}
          <button
            onClick={() => setModalHidden(false)}
            className="bg-white rounded-2xl border-2 border-dashed border-brand-200 flex flex-col items-center justify-center gap-2 min-h-[240px] hover:border-brand-400 hover:bg-brand-50 transition group"
          >
            <div className="w-12 h-12 rounded-full bg-brand-50 group-hover:bg-brand-100 flex items-center justify-center transition">
              <IoMdAdd className="w-6 h-6 text-brand-400 group-hover:text-brand-600" />
            </div>
            <span className="text-xs text-brand-400 group-hover:text-brand-600 font-medium">{t("createProgram")}</span>
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20">
          <button
            onClick={() => setModalHidden(false)}
            className="w-48 h-56 bg-white rounded-2xl border-2 border-dashed border-brand-200 flex flex-col items-center justify-center gap-3 hover:border-brand-400 hover:bg-brand-50 transition group"
          >
            <div className="w-14 h-14 rounded-full bg-brand-50 group-hover:bg-brand-100 flex items-center justify-center transition">
              <IoMdAdd className="w-7 h-7 text-brand-400 group-hover:text-brand-600" />
            </div>
            <span className="text-sm text-brand-400 group-hover:text-brand-600 font-medium">{t("createProgram")}</span>
          </button>
        </div>
      )}

      {/* Create modal */}
      {!modalHidden && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setModalHidden(true)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-900">{t("createProgram")}</h3>
              <button
                onClick={() => setModalHidden(true)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 14 14" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
                </svg>
              </button>
            </div>
            <div className="p-5">
              <DetailsForm passTemplate={null} showPointsInput={true} activeTab="details" UpdateDetails={CreatePassTemplate} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
